/**
 * chrome-links.ts — the single source of truth for the marketing site's CHROME
 * link data (the top nav + the footer), consumed by BOTH chrome variants:
 *
 *   - the dark landing chrome — `features/landing/nav/LandingNav`
 *     + `features/landing/final/LandingFooter` (home route only), and
 *   - the light site-wide chrome — `features/header/Header`
 *     + `features/footer/Footer` (every other route).
 *
 * ## Why this exists
 *
 * The two chromes historically re-declared their link sets independently, so a
 * page could appear in one nav/footer and silently drift out of the other (the
 * landing nav, for instance, lacked the Docs + Benchmarks links the site header
 * carried). Every FULL-PAGE marketing route now has ONE canonical href here
 * (`CHROME_ROUTES`); both chromes reference it, so a new page can no longer be
 * wired into one chrome and forgotten in the other. The primary nav is fully
 * unified (`NAV_LINKS`, variant-tagged, single ordering); the CTAs are unified
 * (`CHROME_CTAS`); the two footers keep their own column layouts — they group
 * links differently on purpose (see `FOOTER_LINK_GROUPS`) — but every footer
 * route href is sourced from `CHROME_ROUTES`, and a drift-guard test proves the
 * shared route set renders in both variants.
 *
 * ## Why label KEYS are not centralised (only hrefs are)
 *
 * The two chromes use DIFFERENT catalog keys for the same destination, and a
 * route's key even differs between the nav and the footer of the *same*
 * variant — e.g. the footer route `/docs` is `landing.final.foot.docs` ("Docs")
 * on the landing footer but `footer.linkDocs` ("Documentation") on the site
 * footer. Collapsing these to one key namespace would require adding/removing
 * i18n catalog entries (out of scope here — see the follow-up note in the task
 * report). So each entry carries the catalog key(s) already in use, per variant
 * and per surface; only the href is centralised. This is a deliberate, modelled
 * divergence, not an oversight.
 */

/** The two chrome variants that render this link data. */
export type ChromeVariant = 'landing' | 'site';

/**
 * Canonical full-page marketing routes. THE registry — every full-page href in
 * either chrome is declared here exactly once. In-page hash anchors (landing
 * sections, `#top` placeholders) and same-origin static assets (`.rss` feeds)
 * are NOT routes and stay inline at their point of use.
 */
export const CHROME_ROUTES = {
  pricing: '/pricing',
  features: '/features',
  howItWorks: '/how-it-works',
  about: '/about',
  onePagers: '/one-pagers',
  compare: '/vs',
  buildVsBuy: '/build-vs-buy',
  benchmarks: '/benchmarks',
  docs: '/docs',
  trust: '/trust',
  contact: '/contact',
  status: '/status',
  // The legal index (`/legal`) fronts the individual legal documents; the
  // footer links IT rather than enumerating them (the cookie policy keeps its
  // own footer link deliberately — an ePrivacy surface a visitor looks for by
  // name). The documents keep their canonical URLs below.
  legal: '/legal',
  terms: '/terms',
  cookies: '/cookies',
  trustSecurity: '/trust/security',
  trustPrivacy: '/trust/privacy',
  trustDpa: '/trust/dpa',
  trustRefunds: '/trust/refunds',
  trustAi: '/trust/ai',
  trustCompliance: '/trust/compliance',
  trustAccessibility: '/trust/accessibility',
  // Docs section landings. These are `/docs/*` slugs served by the DocPage
  // route, registered here because the footer links them as full pages. They
  // replace the `#top` placeholders that shipped when the pages did not exist.
  docsReference: '/docs/reference',
  docsGuides: '/docs/guides',
  docsSdks: '/docs/sdks',
} as const;

export type ChromeRouteId = keyof typeof CHROME_ROUTES;

// ---------------------------------------------------------------------------
// Primary navigation
// ---------------------------------------------------------------------------

/** How a chrome link is rendered. `route` → locale-aware react-router `<Link>`;
 *  `hash` → in-page `<a href="#...">` anchor. */
export type ChromeNavKind = 'route' | 'hash';

export interface ChromeNavLink {
  /** `#anchor` for a hash link, or a `CHROME_ROUTES.*` value for a route. */
  readonly href: string;
  readonly kind: ChromeNavKind;
  /** Which chrome variants surface this nav link. */
  readonly variants: readonly ChromeVariant[];
  /**
   * Per-variant i18n label key. Present for exactly the variants listed in
   * `variants` (enforced by `navLinksFor`). Keys differ per variant by design
   * (see the module header).
   */
  readonly labelKey: Partial<Record<ChromeVariant, string>>;
}

/**
 * The primary-nav link set, in a single canonical order — FIVE destinations,
 * both variants, and that count is the point rather than a coincidence.
 *
 * ## Why five, and why these five
 *
 * The bar previously carried seven links chosen by what existed rather than by
 * what a visitor is looking for, and two of them (Compare, Benchmarks) answer a
 * question nobody has on their first visit: you cannot compare a product you
 * have not understood yet, and a published accuracy score is a proof point for
 * someone already convinced. Both are one click away in the footer, where
 * proof material belongs. The sixth slot (Updates → the public changelog) left
 * with the changelog itself — the page is retired, so the nav no longer
 * carries a liveness signal.
 *
 * What is left is the order a stranger actually reads a company in — what does
 * it do (Features), how does it work (How it works), what does it cost
 * (Pricing), who are you (About), and how do I build with it (Docs). Everything
 * else the site publishes is reachable from the footer.
 *
 * ## The labels are their OWN namespace now
 *
 * They used to be borrowed from the footer and the landing nav. `nav.*` is the
 * top bar's own vocabulary; the footer keeps its own keys because the two
 * surfaces legitimately word the same destination differently.
 */
export const NAV_LINKS: readonly ChromeNavLink[] = [
  {
    href: CHROME_ROUTES.features,
    kind: 'route',
    variants: ['landing', 'site'],
    labelKey: { landing: 'nav.features', site: 'nav.features' },
  },
  {
    href: CHROME_ROUTES.howItWorks,
    kind: 'route',
    variants: ['landing', 'site'],
    labelKey: { landing: 'nav.howItWorks', site: 'nav.howItWorks' },
  },
  {
    href: CHROME_ROUTES.pricing,
    kind: 'route',
    variants: ['landing', 'site'],
    labelKey: { landing: 'nav.pricing', site: 'nav.pricing' },
  },
  {
    href: CHROME_ROUTES.about,
    kind: 'route',
    variants: ['landing', 'site'],
    labelKey: { landing: 'nav.about', site: 'nav.about' },
  },
  {
    href: CHROME_ROUTES.docs,
    kind: 'route',
    variants: ['landing', 'site'],
    labelKey: { landing: 'nav.docs', site: 'nav.docs' },
  },
];

/** A nav link resolved for one variant: its label key is guaranteed present. */
export interface ResolvedChromeNavLink {
  readonly href: string;
  readonly kind: ChromeNavKind;
  readonly labelKey: string;
}

/**
 * The ordered primary-nav links for one chrome variant, each with its
 * variant-specific label key resolved. Throws if a listed variant is missing
 * its label key — a data-integrity guard so a half-declared entry fails loudly
 * rather than rendering a blank link.
 */
export function navLinksFor(variant: ChromeVariant): readonly ResolvedChromeNavLink[] {
  return NAV_LINKS.filter((link) => link.variants.includes(variant)).map((link) => {
    const labelKey = link.labelKey[variant];
    if (!labelKey) {
      throw new Error(
        `chrome nav link "${link.href}" is missing a labelKey for variant "${variant}"`,
      );
    }
    return { href: link.href, kind: link.kind, labelKey };
  });
}

/**
 * The landing-only Product mega-menu — in-page anchors to landing sections with
 * a title + description each. Landing chrome only (there is no equivalent on the
 * light site-wide header), modelled here so ALL chrome link data lives in one
 * place. Its "all features" call-to-action routes to `CHROME_ROUTES.features`.
 */
export interface LandingProductMenuItem {
  /** In-page hash anchor to a landing section. */
  readonly href: string;
  readonly titleKey: string;
  readonly descKey: string;
}

export const LANDING_PRODUCT_MENU: readonly LandingProductMenuItem[] = [
  // The redesign replaced the landing's twelve sections with four addressable
  // bands, so these anchors were re-pointed rather than pruned: `#everywhere`
  // and `#platform` no longer exist on the page, and an anchor that resolves to
  // nothing is a nav link that silently does nothing when clicked.
  {
    href: '#story',
    titleKey: 'landing.nav.menu.story.title',
    descKey: 'landing.nav.menu.story.desc',
  },
  { href: '#how', titleKey: 'landing.nav.menu.how.title', descKey: 'landing.nav.menu.how.desc' },
  {
    href: '#reach',
    titleKey: 'landing.nav.menu.surfaces.title',
    descKey: 'landing.nav.menu.surfaces.desc',
  },
  {
    href: '#control',
    titleKey: 'landing.nav.menu.control.title',
    descKey: 'landing.nav.menu.control.desc',
  },
];

// ---------------------------------------------------------------------------
// Chrome CTAs
// ---------------------------------------------------------------------------

/**
 * What resolves a CTA's destination: `login` → the `appLoginUrl()` site-url
 * helper (the app origin), `contact` → the locale-aware `CHROME_ROUTES.contact`
 * route on this site.
 */
export type ChromeCtaUrl = 'login' | 'contact';

export interface ChromeCta {
  readonly urlKind: ChromeCtaUrl;
  /** Per-variant i18n label key (present for both variants). */
  readonly labelKey: Record<ChromeVariant, string>;
}

/**
 * The two CTAs both chromes render. `secondary` is the returning-user sign-in
 * (→ `appLoginUrl()`); `primary` is "talk to sales" (→ `CHROME_ROUTES.contact`)
 * — sales-led acquisition (operator directive 2026-07-16; no payment processor
 * is connected, so the chrome never advertises a self-serve trial sign-up).
 * Both chromes fire `ANALYTICS_EVENTS.MARKETING_CONTACT_CTA_CLICKED` on the
 * primary CTA with their own `location` tag — that stays at the call site.
 */
export const CHROME_CTAS: { readonly secondary: ChromeCta; readonly primary: ChromeCta } = {
  secondary: {
    urlKind: 'login',
    labelKey: { landing: 'landing.nav.signIn', site: 'header.logIn' },
  },
  primary: {
    urlKind: 'contact',
    labelKey: { landing: 'landing.nav.talkToSales', site: 'header.talkToSales' },
  },
};

/**
 * The set of full-page route hrefs shared by BOTH nav variants — every nav link
 * marked for both `landing` and `site`. The nav drift guard's subject.
 */
export const SHARED_NAV_ROUTE_HREFS: readonly string[] = NAV_LINKS.filter(
  (link) =>
    link.kind === 'route' && link.variants.includes('landing') && link.variants.includes('site'),
)
  .map((link) => link.href)
  .sort();
